/**
 * Product registry — what makes the editor reusable across product types.
 *
 * The 2D editor is already product-agnostic: shapes carry a role in their
 * Fabric `id`/`class` and the backend derives geometry from that. What differs
 * between products is which layouts make sense, which roles exist, which panels
 * to show, and the 3D parameters. All of that lives here.
 *
 * Adding a product means: an entry in PRODUCTS, a JSON schema under
 * ./schemas/ (generated by the backend's `manage.py dump_product_schema` so the
 * two cannot drift), and a builder in the backend's manifold_poc/builders/.
 */

import lightboxSchema from './schemas/lightbox.json';
import keyringHolderSchema from './schemas/keyring_holder.json';
import keyringCorkSchema from './schemas/keyring_cork.json';
import napkinBubbleSchema from './schemas/napkin_bubble.json';
import napkinPanelSchema from './schemas/napkin_panel.json';

/** form2d_data seeded from a schema's own defaults, so the two cannot drift. */
const defaultsFromSchema = (schema, base) => (schema.groups || [])
    .flatMap((group) => group.fields || [])
    .reduce((out, field) => {
        const parts = field.path.split('.');
        let cursor = out;
        parts.slice(0, -1).forEach((part) => {
            cursor[part] = cursor[part] || {};
            cursor = cursor[part];
        });
        cursor[parts[parts.length - 1]] = field.default;
        return out;
    }, { ...base });

/** Layouts whose silhouette is safe to hollow out with a negative offset.
 * Concave ones (dog, heart, comic, claqueta) can split the cavity into
 * unconnected chambers or collapse it entirely, so the lightbox omits them for
 * now — the backend still detects and reports it if one is used anyway. */
const LIGHTBOX_LAYOUTS = ['square', 'circle', 'capsule', 'matricula', 'casa'];

const ALL_LAYOUTS = [
    'square', 'circle', 'dog', 'heart', 'matricula',
    'capsule', 'plant_tag', 'comic', 'casa', 'claqueta',
];

const SHARED_PANELS = [
    'editor_base_layout', 'editor_color', 'editor_params_3d',
    'editor_pieces_3d', 'editor_text', 'editor_history', 'editor_layers',
];

export const PRODUCTS = {
    keyring: {
        key: 'keyring',
        label: 'Llavero',
        routeBase: '/customiz3r_keyring',
        selector: {
            eyebrow: 'Pequeño formato',
            title: 'Customiz3r Keyring',
            description: 'Crea un llavero a tu manera con formas, texto, iconos y colores listo para imprimir en 3D.',
            action: 'Diseñar un llavero',
            icon: 'keyring',
            accent: 'coral',
            features: ['Diseño 2D libre', 'Vista previa 3D', 'Imanes, NFC y silbato integrados'],
        },
        newModelLayout: 'square',
        defaultDesignName: 'Llavero sin título',
        // Copied verbatim from the historical `initialDataModelo3d` so that
        // nothing about the keyring changes.
        defaults: {
            productType: 'keyring',
            holeExists: false,
            holePerforatesDrawings: false,
            thiknessModelo3d: 6,
            drawingDepthMm: 0.75,
            offsetActivated: false,
            stackedTopEngine: 'manifoldcad',
            baseLayout: {
                width: null,
                height: null,
                rounded: false,
                // Per-edge since the radii were split. Null, not 0, on purpose:
                // `withProductDefaults` layers these under whatever was saved,
                // and a 0 here would look like "the user asked for a square
                // edge" to a design that predates the split and still carries
                // only `radius_rounded` + round_top/round_bottom. Absent lets
                // the backend fall back to those. `readEdgeRounding` in the 3D
                // panel does the same fallback for the UI.
                radius_rounded_top: null,
                radius_rounded_bottom: null,
                round_segments_top: null,
                round_segments_bottom: null,
                offsetDrawings: false,
                drawingsOffsetMm: 0,
                dimensionsUnit: 'mm',
                noBackground: false,
                drawingsOverflow: false,
            },
        },
        // null keeps the hand-written ThiknessAndMoreEditor panel: the keyring
        // is in production and its panel is not worth re-risking in the same
        // change that introduces a second product. Porting it is its own step.
        paramSchema: null,
        // La caja para posavasos sí va por schema: es una sección nueva dentro
        // del panel a mano, no hay nada que portar.
        holderSchema: keyringHolderSchema,
        corkSchema: keyringCorkSchema,
        layouts: ALL_LAYOUTS,
        otherLayoutActions: ['offset', 'fill'],
        // Ranura y tornillo avellanado como en la caja de luz, para colgar el
        // llavero (o una placa) de la pared: misma geometría, cortada en la placa.
        // `crocs` es el pin de Crocs (Jibbitz) en la cara de atrás.
        roles: { hole: true, internalElements: true, keyhole: true, screw: true, crocs: true, cable: false },
        rightPanels: [...SHARED_PANELS, 'editor_hole'],
        titles: { editor_hole: 'Elementos internos', editor_params_3d: 'Parámetros 3D' },
        holeTool: {
            tooltip: 'Añadir imanes, NFC, un silbato, un abrebotellas, un colgado de pared o un pin de Crocs al diseño',
            title: 'Elementos internos',
        },
        showEngineSelector: true,
    },

    lightbox: {
        key: 'lightbox',
        label: 'Caja de luz LED',
        routeBase: '/customiz3r_lightbox',
        selector: {
            eyebrow: 'Iluminación decorativa',
            title: 'Customiz3r Lightbox',
            description: 'Convierte tu diseño en una caja de luz LED con frontal, difusor y sistema de colgado personalizados.',
            action: 'Diseñar una lightbox',
            icon: 'lightbox',
            accent: 'violet',
            features: ['Frontal personalizable', 'Difusor de luz', 'Salida de cable'],
        },
        newModelLayout: 'square',
        defaultDesignName: 'Caja de luz sin título',
        defaults: {
            productType: 'lightbox',
            stackedTopEngine: 'manifoldcad',
            offsetActivated: false,
            wallThicknessMm: 2.4,
            floorThicknessMm: 4.0,
            innerDepthMm: 15.0,
            lipHeightMm: 4.0,
            lapDepthMm: 1.2,
            lapClearanceMm: 0.15,
            lipZClearanceMm: 0.1,
            frontThicknessMm: 2.0,
            diffuserThicknessMm: 0.8,
            diffuserColor: '#f2f2f2',
            // Dos láminas translúcidas independientes: la del fondo (con su
            // alcance) y la que va bajo los dibujos, para que un color no
            // transparente de más. Lo que no cubra ninguna lo rellena el cuerpo
            // opaco, así que la banda bajo el arte nunca queda hueca.
            diffuserScope: 'wall_and_unpainted',
            artDiffuserThicknessMm: 0.8,
            // Qué queda detrás de un dibujo con menos profundidad de la normal:
            // 'opaque' lo apaga, 'diffuser' solo lo suaviza.
            shallowFill: 'opaque',
            blackIsOpaque: true,
            clipArtToCavity: false,
            // Circle, slot width and slot length are per-marker: they live in
            // each marker's own id on the canvas, set in the "Colgado" panel.
            // Only the depth of the blind pocket is shared, since every slot
            // cuts into the same floor.
            keyholeLipThicknessMm: 1.0,
            keyholeHeadDepthMm: 2.4,
            // Hacia dónde sale la ranura desde el círculo. "Arriba" es la que
            // cuelga de verdad; con la ranura por debajo la pieza se suelta al
            // bajar. Es solo el defecto: cada marcador lleva el suyo en su id.
            keyholeSlotDirection: 'up',
            // Los dos diámetros del avellanado son por marcador (viven en su id);
            // el ángulo del cono es de la tornillería, no del diseño, así que es
            // el único que se comparte. 90° es el métrico habitual (DIN 965).
            screwCountersinkAngleDeg: 90.0,
            cableExit: 'wall_notch',
            cableEdge: 'bottom',
            cableWidthMm: 8.0,
            cableHeightMm: 4.0,
            cableHoleDiameterMm: 5.0,
            plateGapMm: 5.0,
            baseLayout: {
                width: null,
                height: null,
                // Unlike the keyring, rounding is on by default: the art face
                // prints against the bed, and 0.6mm chamfers the elephant foot.
                rounded: true,
                radius_rounded_top: 0.6,
                radius_rounded_bottom: 0.6,
                round_segments_top: 8,
                round_segments_bottom: 8,
            },
        },
        paramSchema: lightboxSchema,
        layouts: LIGHTBOX_LAYOUTS,
        otherLayoutActions: ['offset', 'fill'],
        // `screw` es el avellanado pasante: la alternativa a la ranura, para
        // atornillar la caja a la pared en vez de colgarla de un tornillo puesto.
        roles: { hole: false, keyhole: true, screw: true, cable: true },
        // Reuses the hole panel slot: for a lightbox that button opens the
        // hanging slot instead of the keyring ring.
        rightPanels: [...SHARED_PANELS, 'editor_hole'],
        titles: {
            editor_params_3d: 'Parámetros de la caja',
            editor_hole: 'Sistema de colgado',
        },
        holeTool: {
            tooltip: 'Colocar la ranura de colgado',
            title: 'Sistema de colgado',
        },
        // The slide direction is per-marker too (each marker records its own),
        // so the shared field would only ever be a default nobody reads back.
        hiddenSchemaFields: ['keyhole_slot_direction'],
        // A lightbox has no Blender recipe, so there is nothing to choose.
        showEngineSelector: false,
    },

    // Servilletero de paredes decoradas: cada pared es un llavero (mismo
    // editor 2D, misma receta de placa) y una base con la unión elegida. El
    // perfil de base es un marcador del lienzo; la servilleta, una guía que no
    // se imprime. Ver docs/technical/servilletero-panel.md.
    napkin_panel: {
        key: 'napkin_panel',
        label: 'Servilletero de paredes',
        routeBase: '/customiz3r_servilletero_panel',
        selector: {
            eyebrow: 'Hostelería y hogar',
            title: 'Servilletero de paredes',
            description: 'Diseña la pared como un llavero —silueta, dibujos, colores— y encájala en una base a la medida de tus servilletas.',
            action: 'Diseñar un servilletero',
            icon: 'flower',
            accent: 'coral',
            features: ['Editor 2D del llavero', 'Cola de milano, pegado o tornillos', 'Guía de la servilleta a escala'],
        },
        newModelLayout: 'square',
        defaultDesignName: 'Servilletero sin título',
        defaults: defaultsFromSchema(napkinPanelSchema, {
            productType: 'napkin_panel',
            holeExists: false,
            holePerforatesDrawings: false,
            offsetActivated: false,
            stackedTopEngine: 'manifoldcad',
            baseLayout: {
                width: null,
                height: null,
                round_segments_top: 8,
                round_segments_bottom: 8,
                offsetDrawings: false,
                drawingsOffsetMm: 0,
                dimensionsUnit: 'mm',
                noBackground: false,
            },
        }),
        paramSchema: napkinPanelSchema,
        layouts: ALL_LAYOUTS,
        otherLayoutActions: ['offset', 'fill'],
        // Los internos (NFC, imanes) van en las dos paredes por ahora; más
        // adelante cada pared podrá tener los suyos.
        roles: { napkinJoint: true, internalElements: true, hole: false, keyhole: false, screw: false, crocs: false, cable: false },
        rightPanels: [...SHARED_PANELS, 'editor_hole'],
        titles: { editor_hole: 'Unión con la base', editor_params_3d: 'Parámetros del servilletero' },
        holeTool: {
            tooltip: 'Perfil de base (la parte de la pared que entra en la base) y elementos internos',
            title: 'Unión con la base',
        },
        showEngineSelector: false,
    },

    // Servilletero de pompas. Es paramétrico: no tiene lienzo 2D ni SVG, así
    // que no usa el Customizer sino su propia página (`BubbleConfigurator`).
    napkin_bubble: {
        key: 'napkin_bubble',
        label: 'Servilletero BUBBLE',
        routeBase: '/customiz3r_servilletero',
        parametric: true,
        selector: {
            eyebrow: 'Hostelería y hogar',
            title: 'Servilletero BUBBLE',
            description: 'Un servilletero de pompas fusionadas a la medida de tus servilletas. Cada semilla da un diseño distinto, siempre imprimible.',
            action: 'Diseñar un servilletero',
            icon: 'bubble',
            accent: 'teal',
            features: ['Medidas de tus servilletas', 'Pompas aleatorias reproducibles', 'Validado para imprimir'],
        },
        defaultDesignName: 'Servilletero sin título',
        defaults: defaultsFromSchema(napkinBubbleSchema, { productType: 'napkin_bubble' }),
        paramSchema: napkinBubbleSchema,
        // Tienen control propio en la página (muestrario y semilla).
        hiddenSchemaFields: ['body_color', 'seed'],
        layouts: [],
        roles: {},
        rightPanels: [],
        titles: {},
        showEngineSelector: false,
    },
};

export const DEFAULT_PRODUCT = 'keyring';

/** Rutas base de los editores, para distinguirlas del resto de páginas. */
export const EDITOR_ROUTE_BASES = Object.values(PRODUCTS).map(p => p.routeBase);

/**
 * Clave de transición de página para una ruta.
 *
 * `/customiz3r_keyring` y `/customiz3r_keyring/123` son la misma página: crear
 * un diseño cambia
 * la URL, y una clave por pathname remontaba el editor entero justo al guardar
 * por primera vez (volviendo a 2D y recargando el lienzo). Cambiar de producto
 * sí cambia la clave, que es cuando la transición tiene sentido.
 */
export const routeTransitionKey = (pathname = '') => (
    EDITOR_ROUTE_BASES.find(base => pathname === base || pathname.startsWith(`${base}/`))
    ?? pathname
);

export const getProduct = (key) => PRODUCTS[key] || PRODUCTS[DEFAULT_PRODUCT];

/** Resolve a product type from stored form2d_data, tolerating junk. */
export const resolveProductType = (form2dData) => {
    const raw = String(form2dData?.productType ?? form2dData?.product_type ?? '')
        .trim()
        .toLowerCase();
    return PRODUCTS[raw] ? raw : DEFAULT_PRODUCT;
};

/**
 * Editor URL for a stored model, based on the product it was saved as.
 *
 * Linking every card to the keyring editor would still work — Customizer redirects when
 * the stored product does not match the route — but it would mount the wrong
 * editor first and bounce, which flickers. Prefer sending people straight there.
 */
/**
 * Name a design gets when it is created without the user typing one.
 *
 * Saving no longer interrupts with a name prompt, so every design needs a
 * name it can be born with. The date keeps a profile full of untitled designs
 * distinguishable until they get renamed from the editor's title.
 */
export const buildDefaultDesignName = (product) => {
    const base = product?.defaultDesignName || product?.label || 'Diseño sin título';
    const fecha = new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    return `${base} · ${fecha}`;
};

/**
 * Editor URL when all we have is an id (order rows, dashboard cards…).
 *
 * Defaults to the keyring editor: without `form2d_data` there is no way to tell
 * which product a design is, and Customizer redirects to the right route once
 * it loads the model. Prefer `editorPathForModel` whenever the model is at hand.
 */
export const editorPathForId = (modelId, productType = DEFAULT_PRODUCT) =>
    `${getProduct(productType).routeBase}/${modelId}`;

export const editorPathForModel = (modelo) => {
    const product = getProduct(resolveProductType(modelo?.form2d_data));
    return `${product.routeBase}/${modelo?.id}`;
};

const isPlainObject = (value) =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

/** Deep merge where `override` wins, used to layer stored data over defaults. */
export const deepMerge = (base, override) => {
    const out = { ...base };
    Object.entries(override || {}).forEach(([key, value]) => {
        out[key] = isPlainObject(value) && isPlainObject(base?.[key])
            ? deepMerge(base[key], value)
            : value;
    });
    return out;
};

/**
 * Fill in a product's defaults under whatever was saved.
 *
 * Needed because loading a model replaces the whole parameter state: a design
 * saved before a parameter existed would otherwise come back with it undefined,
 * and the backend would then have to guess. Stored values always win.
 */
export const withProductDefaults = (form2dData) => {
    const product = getProduct(resolveProductType(form2dData));
    return deepMerge(product.defaults, form2dData || {});
};

/** Flatten a schema's groups into a single field list. */
export const schemaFields = (schema) =>
    (schema?.groups || []).flatMap((group) => group.fields || []);

/** Read a dotted path ('baseLayout.rounded') out of form2d_data. */
export const readPath = (data, path) =>
    String(path).split('.').reduce((cursor, part) => (cursor == null ? undefined : cursor[part]), data);

/** Return a copy of `data` with `path` set to `value`, without mutating. */
export const patchPath = (data, path, value) => {
    const parts = String(path).split('.');
    const out = { ...(data || {}) };
    let cursor = out;
    parts.slice(0, -1).forEach((part) => {
        cursor[part] = isPlainObject(cursor[part]) ? { ...cursor[part] } : {};
        cursor = cursor[part];
    });
    cursor[parts[parts.length - 1]] = value;
    return out;
};

/**
 * The form2d_data paths that require regenerating the 3D model.
 *
 * The keyring's list is explicit because its panel is still hand-written and has
 * no schema; every other product derives it from the schema, so a new parameter
 * lights up the "apply changes" button without touching the Customizer.
 */
const KEYRING_3D_PATHS = [
    'thiknessModelo3d',
    'drawingDepthMm',
    'holePerforatesDrawings',
    'stackedTopEngine',
    'baseLayout.rounded',
    'baseLayout.radius_rounded_top',
    'baseLayout.radius_rounded_bottom',
    'baseLayout.round_segments_top',
    'baseLayout.round_segments_bottom',
    'baseLayout.offsetDrawings',
    'baseLayout.noBackground',
    'baseLayout.drawingsOverflow',
    'baseLayout.drawingsOffsetMm',
    // Legacy shared settings: still listed because a design saved before the
    // per-edge split has only these, and editing one must still regenerate.
    'baseLayout.radius_rounded',
    'baseLayout.round_top',
    'baseLayout.round_bottom',
    'baseLayout.round_segments',
    // La caja para posavasos (`coasterHolder.*`) y el corcho (`cork.*`), desde
    // sus schemas.
    ...[keyringHolderSchema, keyringCorkSchema].flatMap((schema) => schemaFields(schema)
        .filter((field) => field.affects3d !== false)
        .map((field) => field.path)),
];

export const affects3dPaths = (product) => {
    if (!product?.paramSchema) return KEYRING_3D_PATHS;
    return schemaFields(product.paramSchema)
        .filter((field) => field.affects3d !== false)
        .map((field) => field.path);
};

/** Snapshot of everything that would change the generated geometry. */
export const get3dParameterSnapshot = (data = {}, product = getProduct(resolveProductType(data))) =>
    Object.fromEntries(affects3dPaths(product).map((path) => [path, readPath(data, path)]));

export const are3dParametersEqual = (first, second) =>
    first != null && second != null && JSON.stringify(first) === JSON.stringify(second);
